import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { ProductFilters } from "@/types";
import { parseArrayParam, parseNumberParam } from "@/utils/url-params";

const parseUrlToFilters = (searchParams: URLSearchParams): ProductFilters => ({
  searchTerm: searchParams.get("name") || "",
  categories: parseArrayParam(searchParams.get("categories")),
  subCategories: parseArrayParam(searchParams.get("subCategories")),
  brands: parseArrayParam(searchParams.get("brands")),
  countries: parseArrayParam(searchParams.get("countries")),
  suppliers: parseArrayParam(searchParams.get("suppliers")),
  tags: parseArrayParam(searchParams.get("tags")),
  priceFrom: parseNumberParam(searchParams.get("priceFrom")),
  priceTo: parseNumberParam(searchParams.get("priceTo")),
  page: parseNumberParam(searchParams.get("page")) || 1,
  limit: parseNumberParam(searchParams.get("limit")) || 10,
  sort: searchParams.get("sort")
    ? searchParams
        .get("sort")!
        .split(",")
        .map((entry) => {
          const [key, direction] = entry.split(":");
          return { key, direction: direction as "asc" | "desc" };
        })
    : undefined,
  hasScrapedProduct: searchParams.has("hasScrapedProduct")
    ? searchParams.get("hasScrapedProduct") === "true"
    : undefined,
  checkInStock: searchParams.has("checkInStock")
    ? searchParams.get("checkInStock") === "true"
    : undefined,
});

const filtersToUrl = (filters: ProductFilters): URLSearchParams => {
  const params = new URLSearchParams();

  if (filters.searchTerm) params.set("name", filters.searchTerm);
  if (filters.categories?.length)
    params.set("categories", filters.categories.join(","));
  if (filters.subCategories?.length)
    params.set("subCategories", filters.subCategories.join(","));
  if (filters.brands?.length) params.set("brands", filters.brands.join(","));
  if (filters.countries?.length)
    params.set("countries", filters.countries.join(","));
  if (filters.suppliers?.length)
    params.set("suppliers", filters.suppliers.join(","));
  if (filters.tags?.length) params.set("tags", filters.tags.join(","));
  if (filters.priceFrom != null)
    params.set("priceFrom", filters.priceFrom.toString());
  if (filters.priceTo != null)
    params.set("priceTo", filters.priceTo.toString());
  if (filters.page) params.set("page", filters.page.toString());
  if (filters.limit) params.set("limit", filters.limit.toString());
  if (filters.sort?.length)
    params.set(
      "sort",
      filters.sort.map((s) => `${s.key}:${s.direction}`).join(","),
    );
  if (filters.hasScrapedProduct !== undefined)
    params.set("hasScrapedProduct", filters.hasScrapedProduct.toString());
  if (filters.checkInStock !== undefined)
    params.set("checkInStock", filters.checkInStock.toString());

  return params;
};

export function useProductFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(
    () => parseUrlToFilters(searchParams),
    [searchParams],
  );

  const setFilter = useCallback(
    <K extends keyof ProductFilters>(key: K, value: ProductFilters[K]) => {
      const newFilters = { ...parseUrlToFilters(searchParams), [key]: value };

      // Reset page to 1 when any filter changes (except page, limit, and sort)
      if (key !== "page" && key !== "limit" && key !== "sort") {
        newFilters.page = 1;
      }

      setSearchParams(filtersToUrl(newFilters), { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setFilters = useCallback(
    (newFilters: Partial<ProductFilters>) => {
      const currentFilters = parseUrlToFilters(searchParams);
      const mergedFilters = { ...currentFilters, ...newFilters };

      // Reset page to 1 if any filter other than page/limit changed
      const hasFilterChange = Object.keys(newFilters).some(
        (key) => key !== "page" && key !== "limit" && key !== "sort",
      );
      if (hasFilterChange && !("page" in newFilters)) {
        mergedFilters.page = 1;
      }

      setSearchParams(filtersToUrl(mergedFilters), { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setSearchParams(
      filtersToUrl({
        searchTerm: "",
        categories: [],
        subCategories: [],
        brands: [],
        countries: [],
        suppliers: [],
        tags: [],
        priceFrom: undefined,
        priceTo: undefined,
        sort: undefined,
        hasScrapedProduct: undefined,
        checkInStock: undefined,
        page: 1,
        limit: filters.limit,
      }),
      { replace: true },
    );
  }, [setSearchParams, filters.limit]);

  return {
    filters,
    setFilter,
    setFilters,
    clearFilters,
  };
}
